// ── NotesPanel — list + add notes for a sales entity ──
// Notes carry entitytype/entityid AND an accountid, so a parent Account can roll
// up every note logged against it and its child opportunities/contacts (rollup=true).
import { useState, useEffect } from "react";
import { B } from "./theme";
import { Btn, Spinner } from "./ui";
import { useApp } from "./appContext";

const TAG_COLOR = { Account: B.amber, Opportunity: B.cyan, Contact: B.cyanDark, Lead: "#8a8f98" };

export const NotesPanel = ({ entitytype, entityid, accountid, rollup }) => {
  const { api, role, currentUser } = useApp();
  const [notes, setNotes] = useState([]);
  const [usersMap, setUsersMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    const params = rollup ? { accountid } : { entitytype, entityid };
    api.get("getNotes", params)
      .then(d => { setNotes(Array.isArray(d) ? d : []); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  };

  useEffect(() => {
    api.get("getUsers").then(r => {
      const m = {}; (Array.isArray(r) ? r : []).forEach(u => { m[String(u.id)] = u.FullName; }); setUsersMap(m);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if ((rollup && accountid) || (!rollup && entitytype && entityid)) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entitytype, entityid, accountid, rollup]);

  const add = () => {
    if (!text.trim() || saving) return;
    setSaving(true); setErr(null);
    api.post("saveNote", {
      entitytype, entityid, accountid: accountid || null,
      source: "Sales", note: text.trim(),
      notedate: new Date().toISOString().slice(0, 10),
      createdby: currentUser?.name || currentUser?.email || null,
    }).then(() => { setText(""); setSaving(false); load(); })
      .catch(e => { setErr(e.message); setSaving(false); });
  };

  const whenLabel = (n) => {
    const d = n.notedate || n.createdat;
    const s = d ? new Date(d).toLocaleString("en-US", { year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
    const who = usersMap[String(n.createdby)] || n.createdby || "—";
    return s + " · " + who;
  };

  const inputStyle = { width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border-secondary)", borderRadius: 6, resize: "vertical", boxSizing: "border-box", background: "var(--color-background-primary)", color: "var(--color-text-primary)" };

  return (
    <div>
      {role.canEdit && entityid && (
        <div style={{ marginBottom: 12 }}>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={3} placeholder="Add a note…" style={inputStyle} />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 6 }}>
            <Btn variant="primary" disabled={saving || !text.trim()} onClick={add}>{saving ? "Saving…" : "Add Note"}</Btn>
          </div>
          {err && <div style={{ color: "#993C1D", fontSize: 12, marginTop: 4 }}>{err}</div>}
        </div>
      )}
      {loading ? <Spinner />
        : notes.length === 0
          ? <div style={{ fontSize: 13, color: "var(--color-text-secondary)", padding: "8px 2px" }}>No notes yet.</div>
          : <div style={{ maxHeight: 360, overflowY: "auto" }}>
              {notes.map(n => (
                <div key={n.id} style={{ padding: "10px 2px", borderBottom: "0.5px solid var(--color-border-tertiary)" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 8, marginBottom: 3, alignItems: "center" }}>
                    <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>{whenLabel(n)}</span>
                    {rollup && n.entitytype && <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: TAG_COLOR[n.entitytype] || B.cyan, borderRadius: 4, padding: "1px 6px", textTransform: "uppercase", whiteSpace: "nowrap" }}>{n.entitytype}</span>}
                  </div>
                  <div style={{ fontSize: 13, whiteSpace: "pre-wrap" }}>{n.note}</div>
                </div>
              ))}
            </div>}
    </div>
  );
};
